// ADENDA z48 (Diego, 28-oct-2026), proceso: (6) WhatsApp con mensaje listo y (7) «Nueva versión» en un clic. Aquí la regla del
// mensaje (prueba sin pantalla) y la marca de «estoy haciendo la versión N+1» de esta cotización, que sigue al vendedor por las
// pestañas hasta que genera el PDF nuevo. La marca es una comodidad de ESTE navegador (sessionStorage): si se pierde, no se pierde
// nada (la cotización sigue editable y «Generar otra versión» sigue en la pestaña PDF).
import { useEffect, useState } from 'react';
import { fechaHermosillo, fechaLarga, PROMO_MSI_MESES, promoAlDia } from '@cotizador/compartido';
import type { PromoCotizacion } from '@cotizador/compartido';
import { dinero, kwp } from './etiquetas.ts';

/** wa.me: 10 dígitos de México → 52…; con lada de país ya puesta, tal cual; sin teléfono, WhatsApp elige el chat. */
export function enlaceWhatsApp(telefono: string | null | undefined, texto: string): string {
  const d = (telefono ?? '').replace(/\D/g, '');
  const numero = d.length === 10 ? `52${d}` : d.length >= 11 ? d : '';
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

/** La promo en el mensaje de WhatsApp: la MISMA que imprimió ese PDF (su nombre y su fecha, los del día en que se generó). Si esa fecha ya
 *  pasó, no se menciona: un PDF de Buen Fin mandado después no promete una promo vencida ni dice «Buen Fin» fuera de su ventana.
 *  Diego, 3-nov-2026: solo la promo («Promo Buen Fin: 20 % de descuento de contado o 18 meses sin intereses con tarjeta de crédito»), nunca
 *  el descuento de siempre. `con18`: el PDF de esa versión lleva SEGURO los 18 MSI (casa, msiPromoSeguroPorContado); si no, no se prometen. */
export function textoPromoWhatsApp(promo: PromoCotizacion, generadoEn: string, hoy: string, con18 = false): string | null {
  const impresa = promoAlDia(promo, fechaHermosillo(new Date(generadoEn)));
  return hoy > impresa.vence ? null : `Promo ${impresa.nombre}: ${impresa.pct} % de descuento de contado${con18 ? ` o ${PROMO_MSI_MESES} meses sin intereses con tarjeta de crédito` : ''}, hasta el ${fechaLarga(impresa.vence)}`;
}

/** El mensaje corto y de ventas para el cliente: saludo con su nombre, qué se le manda, lo que cobra la opción recomendada y el
 *  asesor. Sin enlace público al PDF (son datos del cliente): el PDF se adjunta en el chat. */
export function mensajeWhatsApp(x: { cliente: string; folio: string; version: number; contado: number | null; kwp: number | null; asesor: string | null; todoEnUno: boolean; promo?: string | null }): string {
  const nombre = x.cliente.trim();
  // Verificador del 2-nov, medido: el mensaje del todo en uno decía «$1,075,700», lo que cobra UN sitio, con un PDF de $1,329,900; y «los cambios que platicamos», sin que los
  // hubiera (es V2 porque antes salió su PDF suelto). En un PDF de varios sitios, ni precio de un sitio ni esa frase. El folio ya dice la versión.
  const que = x.version > 1 && !x.todoEnUno ? `la versión ${x.version} de su propuesta de Solarix Energy (${x.folio}), con los cambios que platicamos` : `su propuesta de Solarix Energy (${x.folio})`;
  // La promo de contado (2-nov) va pegada al precio de la recomendada: en el todo en uno no hay precio de un sitio, ni promo.
  const precio = x.contado !== null && !x.todoEnUno ? `La opción que le recomendamos${x.kwp !== null ? ` (${kwp(x.kwp)})` : ''}: ${dinero(x.contado)} de contado, IVA incluido.${x.promo ? ` ${x.promo}.` : ''}` : '';
  const firma = x.asesor ? `${x.asesor.trim()} · Solarix Energy` : 'Solarix Energy';
  return [`Hola${nombre ? ` ${nombre}` : ''}, le comparto ${que}. Se la adjunto en PDF.`, precio, 'Cualquier duda, con gusto se la resuelvo.', firma].filter(Boolean).join('\n');
}

const EVENTO = 'cotizador:nueva-version';
const llave = (id: string) => `cotizador:nueva-version:${id}`;

/** «Hacer cambios (nueva versión)»: desde qué versión se está haciendo la siguiente (null = no se está haciendo). */
export function leerNuevaVersion(id: string): number | null {
  try {
    const v = Number(window.sessionStorage.getItem(llave(id)));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function marcarNuevaVersion(id: string, desde: number | null): void {
  try {
    if (desde === null) window.sessionStorage.removeItem(llave(id));
    else window.sessionStorage.setItem(llave(id), String(desde));
  } catch {
    /* sin almacenamiento: la banda no sale, todo lo demás sigue igual */
  }
  window.dispatchEvent(new Event(EVENTO));
}

/** La marca de esta cotización; se borra sola cuando ya hay una versión más nueva que la de la marca (ya se generó). */
export function usarNuevaVersion(id: string, versionActual: number): number | null {
  const [desde, setDesde] = useState(() => leerNuevaVersion(id));
  useEffect(() => {
    const leer = () => setDesde(leerNuevaVersion(id));
    window.addEventListener(EVENTO, leer);
    return () => window.removeEventListener(EVENTO, leer);
  }, [id]);
  useEffect(() => {
    if (desde !== null && versionActual > desde) marcarNuevaVersion(id, null);
  }, [id, desde, versionActual]);
  return desde !== null && versionActual <= desde ? desde : null;
}
